// app/src/lib/scheduleMap.js
// Bidirectional map between the tracking-cadence slider index and the
// declarative schedule spec ({type, params}) that chatboard persists to
// Postgres and propagates to BQ contracts.metadata. See the schedule
// renderer design (Sooth-Labs/sooth#593).
// Index order is the slider order; keep CADENCES index-parallel with the
// App.jsx TRACKING_CAD / TRACKING_CAD_SHORT arrays.

import contractStatus from "../../../shared/contractStatus.json";

// One entry per slider tick, in slider order. `spec` is the forward mapping.
export const CADENCES = [
  { label: "Never", short: "Never", spec: { type: "resolution_only", params: {} } },
  { label: "On Triggers", short: "On Triggers", spec: { type: "triggers", params: {} } },
  {
    label: "Hourly",
    short: "Hourly",
    spec: { type: "fixed_interval", params: { interval_hours: 1 } },
  },
  {
    label: "Daily",
    short: "Daily",
    spec: { type: "fixed_interval", params: { interval_hours: 24 } },
  },
  {
    label: "Weekly",
    short: "Weekly",
    spec: { type: "fixed_interval", params: { interval_hours: 168 } },
  },
  {
    label: "Monthly",
    short: "Monthly",
    spec: { type: "fixed_interval", params: { interval_hours: 720 } },
  },
];

// Dev-only console.warn. specToCadIdx runs on every render, so out-of-band
// specs are logged for debugging but must not spam production consoles.
// import.meta.env.DEV is true under Vite dev + vitest, false in prod builds.
function devWarn(...args) {
  if (import.meta.env?.DEV) console.warn(...args);
}

// Which forecaster the Observer should run for an auto-reforecast, carried in
// spec.params.engine. Tier tags (not concrete runner names) so it survives
// forecaster renames — the scheduler resolves the tag to the current tier
// default exactly like the customer-facing selector does. Lands in BQ
// contracts.metadata.schedule (whole-object jsonb) for the Observer to read.
export const REFORECAST_ENGINES = ["quick", "deep"];
export const DEFAULT_REFORECAST_ENGINE = "quick";

// Fresh object each call so callers can't mutate CADENCES. `engine` is optional:
// when a valid tier tag is passed it's stamped into params.engine; otherwise the
// params are left as-is (so a bare cadIdxToSpec(idx) is unchanged, and only an
// explicit engine choice is persisted).
export function cadIdxToSpec(cadIdx, engine) {
  const c = CADENCES[cadIdx] || CADENCES[0];
  const params = { ...c.spec.params };
  if (REFORECAST_ENGINES.includes(engine)) params.engine = engine;
  return { type: c.spec.type, params };
}

// Read the reforecast engine tier from a schedule spec, defaulting to 'quick'
// when absent/invalid (older specs predate the field). Mirrors specToCadIdx.
export function specToEngine(spec) {
  const e = spec && spec.params && spec.params.engine;
  return REFORECAST_ENGINES.includes(e) ? e : DEFAULT_REFORECAST_ENGINE;
}

// Reverse map. NULL / non-object / array / missing-empty-or-non-string type /
// resolution_only → None(0, untracked). Unrecognized fixed_interval interval
// snaps to the nearest preset (display only — the stored spec is never
// rewritten by a read). Any OTHER non-resolution_only type (unknown string, or
// fixed_interval with bad params) maps to a TRACKED cadence (Events=1), so the
// slider's tracked state matches the server predicate isTracked() (a non-empty
// string type other than 'resolution_only'). Logs out-of-band specs (dev-only).
// Contract policy statuses that mean "no live re-forecasting" — the schedule
// spec that survives on a terminal contract is stale, so the cadence reads as
// None regardless of that spec (symptom A: a
// deleted contract kept showing "Weekly"). Mirrors the server isTracked guard.
//
// The vocabulary is shared/contractStatus.json (owner-map REG-1), the same file
// server/lib/tracking.js reads: when this set was a hand copy, `voided` joined
// the server's set without joining this one, and a voided contract kept showing
// a live cadence — the symptom this set exists to prevent, one status later.
const TERMINAL_CONTRACT_STATUSES = new Set(contractStatus.terminal);

export function specToCadIdx(spec, contractStatus) {
  // Status wins over the stale spec: a deleted/resolved contract is None(0).
  if (contractStatus && TERMINAL_CONTRACT_STATUSES.has(contractStatus)) return 0;
  // Mirror isTracked: only a non-empty STRING type can be tracked. A missing,
  // empty, non-string (e.g. numeric), or array-shaped type → None(0).
  if (
    !spec ||
    typeof spec !== "object" ||
    Array.isArray(spec) ||
    typeof spec.type !== "string" ||
    spec.type === ""
  )
    return 0;
  if (spec.type === "resolution_only") return 0;
  if (spec.type === "triggers") return 1;
  // Legacy 'adaptive' schedules (the cadence was removed) → On Triggers(1),
  // matching the server's isTracked predicate for any non-resolution_only type.
  if (spec.type === "adaptive") return 1;
  if (spec.type === "fixed_interval") {
    const h = spec.params && spec.params.interval_hours;
    if (!Number.isFinite(h)) {
      // Tracked type but unusable params — keep the UI tracked (On Triggers) to
      // match isTracked(); don't fall through to a misleading fixed cadence.
      devWarn(
        "[scheduleMap] fixed_interval with invalid interval_hours",
        h,
        "→ On Triggers (tracked)",
      );
      return 1;
    }
    // Quarterly was removed; a legacy quarterly spec (2160h) snaps to the
    // nearest remaining preset (Monthly) for display only — the stored spec is
    // never rewritten by a read.
    const fixedIdxs = [2, 3, 4, 5];
    let best = 2,
      bestDiff = Infinity;
    for (const i of fixedIdxs) {
      const diff = Math.abs(CADENCES[i].spec.params.interval_hours - h);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = i;
      }
    }
    if (bestDiff !== 0) {
      devWarn(
        "[scheduleMap] unrecognized fixed_interval interval_hours",
        h,
        "→ nearest preset",
        CADENCES[best].label,
      );
    }
    return best;
  }
  // Unknown but non-resolution_only type: the server treats it as TRACKED
  // (isTracked = type !== 'resolution_only'), so keep the slider tracked too.
  devWarn("[scheduleMap] unknown schedule type", spec.type, "→ On Triggers (tracked)");
  return 1;
}
